import { useState } from "react";
import toast from "react-hot-toast";
import { Briefcase, Plus } from "lucide-react";
import {
  useClientServices,
  useUpdateClientService,
  CLIENT_SERVICE_STATUS_LABEL,
  ClientServiceStatus,
  formatPrice,
} from "@/api/services";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import ContractServiceModal from "./ContractServiceModal";

const STATUS_STYLE: Record<ClientServiceStatus, string> = {
  ACTIVE: "bg-green-100 text-green-700",
  PAUSED: "bg-amber-100 text-amber-700",
  CANCELLED: "bg-gray-100 text-gray-400",
  COMPLETED: "bg-blue-100 text-blue-700",
};

const STATUS_OPTIONS = Object.keys(CLIENT_SERVICE_STATUS_LABEL) as ClientServiceStatus[];

export default function ServicesSection({ clientId }: { clientId: string }) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { data, isLoading } = useClientServices(clientId);
  const updateClientService = useUpdateClientService();
  const [showContract, setShowContract] = useState(false);

  const handleStatusChange = async (contractId: string, status: ClientServiceStatus) => {
    try {
      await updateClientService.mutateAsync({ clientId, contractId, status });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Servicios contratados</h2>
        {hasPermission("services.create") && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
            onClick={() => setShowContract(true)}
          >
            <Plus size={14} /> Contratar servicio
          </button>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {isLoading && <p className="text-sm text-gray-400">Cargando servicios...</p>}
        {!isLoading && (!data?.data || data.data.length === 0) && (
          <p className="text-sm text-gray-400">Este cliente aún no tiene servicios contratados.</p>
        )}
        {data?.data.map((contract) => (
          <div key={contract.id} className="flex items-center justify-between rounded-md border border-gray-100 p-3">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
                <Briefcase size={15} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{contract.service.name}</p>
                <p className="text-xs text-gray-500">
                  {formatPrice(contract.price ?? contract.service.defaultPrice)} · Desde {contract.startDate}
                  {contract.endDate && ` · Hasta ${contract.endDate}`}
                </p>
              </div>
            </div>
            {hasPermission("services.update") ? (
              <select
                className={`shrink-0 rounded-full border-0 px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[contract.status]}`}
                value={contract.status}
                onChange={(e) => handleStatusChange(contract.id, e.target.value as ClientServiceStatus)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {CLIENT_SERVICE_STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            ) : (
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[contract.status]}`}>
                {CLIENT_SERVICE_STATUS_LABEL[contract.status]}
              </span>
            )}
          </div>
        ))}
      </div>

      {showContract && <ContractServiceModal clientId={clientId} onClose={() => setShowContract(false)} />}
    </div>
  );
}
